<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {storeToRefs} from "pinia";
import moment from "moment";

import {useMainStore} from "@/stores/mainStore";
import {oplitClient} from "@/api";
import {
  DATE_FORMATS,
  DATE_WITH_TIME_FORMAT,
  TIME_DEFAULT_FORMAT,
} from "@oplit/shared-module";
import {useSchedulingStore} from "@/stores/schedulingStore";

const {t} = useI18n();

const mainStore = useMainStore();
const schedulingStore = useSchedulingStore();

const {shouldReloadSchedulingSimulationUpdateDate} = storeToRefs(mainStore);
const {selectedSimulation} = storeToRefs(schedulingStore);

const schedulingSimulationLastImportData = ref<{
  import_date: string;
  import_type: string;
}>(undefined);

const displayedUpdateDate = computed<string>(() => {
  if (!schedulingSimulationLastImportData.value) return "";

  const date = moment
    .utc(
      schedulingSimulationLastImportData.value.import_date,
      DATE_WITH_TIME_FORMAT,
    )
    .toDate();
  // toTimeString() will output the local 24H date as well as the timezone, such as `HH:mm:ss GMT+n`
  const [localTime] = date.toTimeString().split(" ");

  const isoDateString = moment.utc(date).format(DATE_FORMATS.FR);
  const localTimeString = moment(localTime, "HH:mm:ss").format(
    TIME_DEFAULT_FORMAT,
  );

  return `${isoDateString} ${t("Commons.at")} ${localTimeString}`;
});
const displayedUpdateType = computed<string>(() => {
  if (!schedulingSimulationLastImportData.value) return "";

  return schedulingSimulationLastImportData.value.import_type === "ofs"
    ? t("navbar.load_file_import")
    : t("navbar.prod_file_import");
});
const displayedTooltipUpdateDate = computed<string>(() => {
  if (!schedulingSimulationLastImportData.value) return "";

  return `${t("FollowUp.data_updated_on")} : ${displayedUpdateDate.value}`;
});

onMounted(() => loadSchedulingSimulationLastImportData());

watch(
  () => [
    shouldReloadSchedulingSimulationUpdateDate.value,
    selectedSimulation.value.id,
  ],
  () => loadSchedulingSimulationLastImportData(),
);

async function loadSchedulingSimulationLastImportData() {
  const [{import_date, import_type}] =
    await oplitClient.apiSchedulingGetSimulationLastImportData({
      simulation_id: selectedSimulation.value.id,
    });

  schedulingSimulationLastImportData.value = {import_date, import_type};
}
</script>

<template>
  <template v-if="schedulingSimulationLastImportData">
    <div v-tooltip="displayedUpdateType" class="navbar-updated-at__container">
      <span class="text-12 text-neutral-tertiary">
        {{ t("FollowUp.data_updated_on") }} :
      </span>

      <span class="text-14 text-neutral-secondary">
        {{ displayedUpdateDate }}
      </span>
    </div>

    <vue-feather
      v-tooltip="displayedTooltipUpdateDate"
      stroke="rgb(var(--v-theme-grey-500))"
      class="navbar-updated-at__icon"
      type="info"
      size="18"
    />
  </template>
</template>

<style scoped lang="scss">
@use "@/scss/mixins/responsive.scss";

@include responsive.media-max("desktop-down") {
  .navbar-updated-at__container {
    display: none;
  }

  .navbar-updated-at__icon {
    flex-shrink: 0;
  }
}

@include responsive.media-min("desktop-down") {
  .navbar-updated-at__container {
    display: flex;
    flex-direction: column;
    min-width: 136px;
  }

  .navbar-updated-at__icon {
    display: none;
  }
}
</style>
